import React from 'react';
import { motion } from 'framer-motion';
import { FlaskConical, Lock, ArrowUpRight } from 'lucide-react';
import { LAB_PROJECTS } from '../../utils/constants';

// The Lab — live work-in-progress. Data lives in constants.js (LAB_PROJECTS);
// adding a project = adding one entry there. Veiled entries (veil: true) show
// their codename + tagline until the public reveal.

const ease = [0.16, 1, 0.3, 1];

const Meter = ({ label, pct, done }) => (
  <div className="flex items-center gap-3 py-1.5">
    <span className="w-36 shrink-0 text-[13px] text-zinc-400">{label}</span>
    <div className="h-[7px] flex-1 overflow-hidden rounded-full bg-white/[0.07]">
      <motion.div
        className="h-full rounded-full"
        style={{
          background: done
            ? 'linear-gradient(90deg,#34d399,#10b981)'
            : 'linear-gradient(90deg,#f0abfc,#d946ef)',
        }}
        initial={{ width: 0 }}
        whileInView={{ width: `${pct}%` }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, ease }}
      />
    </div>
    <span className="w-12 shrink-0 text-right font-mono text-[11px] text-zinc-500">
      {pct}%
    </span>
  </div>
);

const LabCard = ({ project, index }) => (
  <motion.article
    className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-7 backdrop-blur-sm"
    initial={{ opacity: 0, y: 26 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.6, delay: index * 0.08, ease }}
  >
    {/* top row: codename + status */}
    <div className="mb-5 flex flex-wrap items-center gap-2">
      <span className="rounded-full border border-fuchsia-400/30 bg-fuchsia-500/10 px-3 py-1 font-mono text-[11px] tracking-widest text-fuchsia-300">
        {project.codename}
      </span>
      <span className="rounded-full border border-white/10 px-3 py-1 text-[11px] text-zinc-400">
        {project.category}
      </span>
      <span className="ml-auto rounded-full bg-emerald-500/10 px-3 py-1 text-[11px] font-medium text-emerald-300">
        {project.status} · {project.eta}
      </span>
    </div>

    {/* title / veil */}
    {project.veil ? (
      <h3 className="mb-2 text-2xl font-semibold leading-snug text-white md:text-[27px]">
        <span className="bg-gradient-to-r from-fuchsia-300 to-fuchsia-500 bg-clip-text text-transparent">
          {project.tagline}
        </span>
      </h3>
    ) : (
      <h3 className="mb-2 text-2xl font-semibold text-white md:text-[27px]">
        {project.title}
        <span className="ml-3 text-base font-normal text-zinc-400">
          {project.tagline}
        </span>
      </h3>
    )}

    <p className="mb-6 max-w-2xl text-[15px] leading-relaxed text-zinc-400">
      {project.description}
    </p>

    {/* progress meters */}
    <div className="mb-6 rounded-xl border border-white/[0.06] bg-black/20 px-5 py-4">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
        Honestly, how far along it is
      </div>
      {project.progress.map((p) => (
        <Meter key={p.label} label={p.label} pct={p.pct} done={p.pct === 100} />
      ))}
    </div>

    {/* tech + footnote */}
    <div className="flex flex-wrap items-center gap-2">
      {project.techStack.map((t) => (
        <span
          key={t}
          className="rounded-md border border-white/10 bg-white/[0.04] px-2.5 py-1 font-mono text-[11px] text-zinc-300"
        >
          {t}
        </span>
      ))}
      {project.link && (
        <a
          href={project.link}
          className="ml-auto inline-flex items-center gap-1 text-[13px] text-fuchsia-300 hover:text-fuchsia-200"
        >
          Follow along <ArrowUpRight className="h-3.5 w-3.5" />
        </a>
      )}
    </div>

    {project.highlight && (
      <div className="mt-5 flex items-start gap-2 border-t border-white/[0.06] pt-4 text-[13px] text-zinc-500">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-zinc-600" />
        <span>{project.highlight}</span>
      </div>
    )}
  </motion.article>
);

const LabPage = () => (
  <div
    className="min-h-screen pb-28 pt-32"
    style={{ background: 'var(--bg-primary, #07070b)' }}
  >
    <div className="mx-auto w-full max-w-5xl px-6">
      <motion.header
        className="mb-14"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease }}
      >
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-1.5 text-[12px] uppercase tracking-[0.16em] text-zinc-400">
          <FlaskConical className="h-3.5 w-3.5 text-fuchsia-400" />
          Work in progress
        </div>
        <h1 className="mb-4 text-4xl font-bold leading-tight text-white md:text-5xl">
          The <span className="bg-gradient-to-r from-fuchsia-300 to-fuchsia-500 bg-clip-text text-transparent">Lab</span>
        </h1>
        <p className="max-w-2xl text-lg text-zinc-400">
          What we&apos;re building right now — real projects, real progress bars,
          updated as the work happens. No vaporware, no padded timelines.
        </p>
      </motion.header>

      <div className="grid gap-6">
        {LAB_PROJECTS.map((p, i) => (
          <LabCard key={p.codename} project={p} index={i} />
        ))}
      </div>

      <motion.p
        className="mt-12 text-center text-[13px] text-zinc-600"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        More projects step out of the lab as they&apos;re ready to be seen.
      </motion.p>
    </div>
  </div>
);

export default LabPage;
